import { Type } from '@sinclair/typebox';
import { paginatedSchema } from './pagination.js';
import { EmailSchema, IsoDateTimeSchema, NonEmptyStringSchema, Sha256Schema, UuidSchema } from './primitives.js';
import { literalUnion } from './union.js';
/**
 * Contracts for the photo application, mounted at `/photos`.
 *
 * Three things shape the upload half of it:
 *
 *  - **Nothing arrives in one request.** The web server caps a body at 512 MB
 *    and Apache cuts every request at 360 seconds, so a video arrives as 8 MiB
 *    parts and a dropped connection resumes rather than restarts.
 *  - **Nothing survives between requests.** Passenger reclaims an idle app
 *    after 300 seconds, so the progress of an upload is rows and never memory.
 *  - **The hash comes first.** The client hashes the file before sending a
 *    byte, so a duplicate costs one small request instead of a transfer.
 */
// ---------------------------------------------------------------------------
// What may be uploaded
// ---------------------------------------------------------------------------
/** Whether an item is played or looked at. Derived from the media type, never taken from the client. */
export const MEDIA_KINDS = ['photo', 'video'];
export const MediaKindSchema = literalUnion(MEDIA_KINDS);
/**
 * The media types the gallery accepts, and which kind each one is.
 *
 * A map rather than two lists, because the kind is a fact about the type and
 * storing it separately is how the two drift. The client reads the keys for its
 * file picker's `accept`; the server reads the values and never believes a
 * `kind` sent to it.
 *
 * HEIC is here because it is what an iPhone produces by default, and leaving it
 * out would reject most of the photos on most of the phones this is for.
 */
export const MEDIA_TYPE_KINDS = Object.freeze({
    'image/jpeg': 'photo',
    'image/png': 'photo',
    'image/webp': 'photo',
    'image/avif': 'photo',
    'image/gif': 'photo',
    'image/heic': 'photo',
    'image/heif': 'photo',
    'video/mp4': 'video',
    'video/quicktime': 'video',
    'video/webm': 'video',
});
/** For a file picker's `accept`, and for the error message that lists what would have worked. */
export const ALLOWED_MEDIA_TYPES = Object.freeze(Object.keys(MEDIA_TYPE_KINDS));
/** The kind a media type is, or `undefined` when it is not one we take. */
export function mediaKindFor(mimeType) {
    return MEDIA_TYPE_KINDS[mimeType.split(';')[0]?.trim().toLowerCase() ?? ''];
}
// ---------------------------------------------------------------------------
// Sizes
// ---------------------------------------------------------------------------
/**
 * 8 MiB of plaintext per part.
 *
 * Comfortably under the web server's 512 MB body cap and short enough that one
 * part is nowhere near the 360-second request limit even on a phone's uplink.
 * It is also an exact multiple of the cipher's 64 KiB chunk, which is what lets
 * a part map onto a whole number of chunks and be sealed without knowing
 * anything about the parts around it.
 *
 * Above S3's 5 MB minimum for every part but the last, which is not a
 * coincidence: each part is one multipart upload part.
 */
export const MEDIA_UPLOAD_PART_BYTES = 8 * 1024 * 1024;
/** The largest single file, matching the web server's own cap. */
export const MAX_MEDIA_BYTES = 512 * 1024 * 1024;
/** How many parts a file of this size takes. Both halves compute it; neither sends it. */
export function partCountFor(byteSize) {
    return Math.max(1, Math.ceil(byteSize / MEDIA_UPLOAD_PART_BYTES));
}
// ---------------------------------------------------------------------------
// The duplicate check
// ---------------------------------------------------------------------------
/** Asked before any bytes move, with a hash the client computed itself. */
export const UploadCheckBodySchema = Type.Object({
    sha256: Sha256Schema,
}, { additionalProperties: false });
/**
 * Whether the library already holds these exact bytes.
 *
 * Per account and never global: answering across accounts would let one user
 * learn that another holds a particular file.
 */
export const UploadCheckResponseSchema = Type.Object({
    duplicate: Type.Boolean(),
    /** The item already holding those bytes, so the client can offer to show it. Null when there is none. */
    mediaItemId: Type.Union([UuidSchema, Type.Null()]),
    /**
     * Whether that item is in the trash.
     *
     * The duplicate check deliberately sees trashed items - they still hold
     * their storage key, so re-uploading the same file would collide - and
     * without this field the answer is indistinguishable from a photo sitting
     * in the gallery. Somebody who deleted a photo and dragged it back in would
     * be told it is already in the library and then fail to find it, which
     * reads as the upload having silently done nothing.
     */
    trashed: Type.Boolean(),
}, { additionalProperties: false });
// ---------------------------------------------------------------------------
// The session
// ---------------------------------------------------------------------------
export const UPLOAD_SESSION_STATUSES = ['open', 'completed', 'aborted'];
export const UploadSessionStatusSchema = literalUnion(UPLOAD_SESSION_STATUSES);
export const BeginUploadBodySchema = Type.Object({
    filename: Type.String({ minLength: 1, maxLength: 255 }),
    mimeType: NonEmptyStringSchema,
    byteSize: Type.Integer({ minimum: 0, maximum: MAX_MEDIA_BYTES }),
    /** Of the plaintext, computed before the upload begins. The storage key is built from it. */
    sha256: Sha256Schema,
}, { additionalProperties: false });
/**
 * An upload in progress, as the client needs to see it.
 *
 * `receivedParts` is what makes resumption work: a client that lost its
 * connection asks for the session again and is told exactly which parts still
 * have to be sent, rather than starting over.
 */
export const UploadSessionSchema = Type.Object({
    id: UuidSchema,
    status: UploadSessionStatusSchema,
    byteSize: Type.Integer({ minimum: 0 }),
    sha256: Sha256Schema,
    /** Bytes per part. Sent rather than assumed, so the constant can change without stranding an old client. */
    partBytes: Type.Integer({ minimum: 1 }),
    partCount: Type.Integer({ minimum: 1 }),
    /** 1-based part numbers already stored, ascending. */
    receivedParts: Type.Array(Type.Integer({ minimum: 1 })),
    expiresAt: IsoDateTimeSchema,
}, { additionalProperties: false });
// ---------------------------------------------------------------------------
// The item
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Derivatives
// ---------------------------------------------------------------------------
/**
 * The renditions made from an original.
 *
 * A closed list here even though `libs/storage` validates the fourth segment of
 * a key with a pattern, and the two levels are doing different jobs: the
 * pattern makes a variant structurally incapable of escaping its prefix, this
 * makes a typo a rejected request rather than an orphaned object nothing ever
 * looks for again.
 *
 *  - `thumb` — the grid. Small enough that a phone can hold a screenful.
 *  - `display` — the full-screen view, short of the original's full size.
 *  - `poster` — the first frame of a video, so a tile has something to show.
 */
export const MEDIA_VARIANTS = ['thumb', 'display', 'poster'];
export const MediaVariantSchema = literalUnion(MEDIA_VARIANTS);
/**
 * How long a deleted item stays recoverable before the sweep destroys it.
 *
 * One constant, because three things in different processes have to agree about
 * it: the cron sweep that chooses what to purge, the screen that counts the
 * window down, and the sentence explaining what pressing delete does. Two of
 * those carrying a hardcoded 30 is how a promise made in an interface stops
 * matching the job that is supposed to keep it.
 *
 * Counted from `media_items.deleted_at`, which is the only record of when the
 * window opened - so restoring something and deleting it again starts the
 * thirty days over, which is what anybody would expect it to do.
 */
export const TRASH_RETENTION_DAYS = 30;
/**
 * The largest a rendition may be.
 *
 * Generous for a thumbnail and mean for anything pretending to be one. A
 * derivative bigger than this is either a mistake or an attempt to store the
 * original twice, and both are worth refusing.
 */
export const MAX_DERIVATIVE_BYTES = 8 * 1024 * 1024;
/** What a client may send as a rendition. Narrower than the upload list: no HEIC, no video. */
export const ALLOWED_DERIVATIVE_TYPES = Object.freeze(['image/jpeg', 'image/webp', 'image/avif', 'image/png']);
/** Header carrying the pixel size of a rendition, which its bytes may not state for every format. */
export const DERIVATIVE_WIDTH_HEADER = 'x-hb-width';
export const DERIVATIVE_HEIGHT_HEADER = 'x-hb-height';
/**
 * Header carrying the perceptual hash of the image a rendition was drawn from.
 *
 * It rides on the **thumbnail** upload rather than costing a request of its own,
 * and that is not merely thrift: both are products of the single
 * `createImageBitmap` the client already performs, so they are available at
 * exactly the same moment and for exactly the same files. A photo therefore has
 * a perceptual hash precisely when a browser could decode it - HEIC on a desktop
 * has neither a thumbnail nor a hash, and a video has neither by definition.
 *
 * Sixteen lowercase hex characters, because a 64-bit value **cannot survive as a
 * JSON number**: `Number.MAX_SAFE_INTEGER` is 2^53-1, so the low bits of a hash
 * above it are silently rounded away, and two different pictures arrive
 * identical. See {@link PhashSchema}.
 */
export const MEDIA_PHASH_HEADER = 'x-hb-phash';
/**
 * The headers of `PUT /photos/media/:id/derivatives/:variant` - declared on the
 * route so the API surface an installed app depends on records them.
 *
 * A header is the one thing a client sends that nothing refuses when it
 * changes: rename one here and every phone goes on sending the old name, the
 * server stops reading it, and renditions quietly lose their size or their
 * perceptual hash. A declared header is one `api-surface.test.ts` can hold the
 * API to.
 *
 * Every one optional and any string, on purpose. The route reads them
 * leniently and ignores a value it cannot use, and a declaration that refused
 * one would turn a rendition stored without its hash into a 400.
 */
export const DerivativeHeadersSchema = Type.Object({
    [DERIVATIVE_WIDTH_HEADER]: Type.Optional(Type.String()),
    [DERIVATIVE_HEIGHT_HEADER]: Type.Optional(Type.String()),
    [MEDIA_PHASH_HEADER]: Type.Optional(Type.String()),
});
/** One stored rendition. */
export const MediaDerivativeSchema = Type.Object({
    id: UuidSchema,
    mediaItemId: UuidSchema,
    variant: MediaVariantSchema,
    mimeType: NonEmptyStringSchema,
    byteSize: Type.Integer({ minimum: 0 }),
    width: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    height: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    createdAt: IsoDateTimeSchema,
}, { additionalProperties: false });
// ---------------------------------------------------------------------------
// Reading one back
// ---------------------------------------------------------------------------
/** How many objects one access request may ask about. A gallery page draws about fifty. */
export const MAX_MEDIA_ACCESS_BATCH = 100;
/**
 * Everything needed to fetch an object and open it.
 *
 * The URL serves ciphertext and the key opens it, and the two travel together
 * because the service worker needs both to answer a single `<img src>`. That
 * means the item key reaches the browser - which is the design: the browser is
 * about to display the photo, so it is not a new exposure, and it is what keeps
 * the bytes flowing from the CDN instead of through the API.
 *
 * Never cacheable. The response carries key material and a URL that expires.
 */
export const MediaAccessSchema = Type.Object({
    mediaItemId: UuidSchema,
    /**
     * Which rendition this is, or null for the original.
     *
     * Echoed rather than inferred, because an id that resolved to no such
     * variant is absent from the batch and nothing else would say which of the
     * two answers about one item came back.
     */
    variant: Type.Union([MediaVariantSchema, Type.Null()]),
    /** Presigned, short-lived, and pointing at ciphertext. */
    url: NonEmptyStringSchema,
    expiresAt: IsoDateTimeSchema,
    /** The item key, base64. 32 bytes decoded. */
    key: NonEmptyStringSchema,
    /** Plaintext length. Range arithmetic needs it, and a range request cannot say where an object ends. */
    byteSize: Type.Integer({ minimum: 0 }),
    /** The object's chunk size. A wrong value fails loudly, because the header it implies is authenticated. */
    chunkBytes: Type.Integer({ minimum: 1024 }),
    mimeType: NonEmptyStringSchema,
}, { additionalProperties: false });
/**
 * One thing to open: an item, and optionally which rendition of it.
 *
 * A pair rather than an id, because a grid asks for `thumb` and the full-screen
 * view asks for the original, and both are the same item. It mirrors the
 * storage key exactly - `photos/<owner>/<sha256>` with an optional fourth
 * `<variant>` segment - and it is what `/media/<id>` and `/media/<id>/<variant>`
 * parse into in the service worker.
 */
export const MediaAccessTargetSchema = Type.Object({
    mediaItemId: UuidSchema,
    /** Omitted for the original. A variant the item has no rendition for is absent from the answer. */
    variant: Type.Optional(MediaVariantSchema),
}, { additionalProperties: false });
/**
 * Asking about several at once.
 *
 * A gallery page draws about fifty thumbnails, and fifty round trips to unwrap
 * one account key fifty times would be the obvious way to make scrolling feel
 * slow.
 */
export const MediaAccessRequestSchema = Type.Object({
    targets: Type.Array(MediaAccessTargetSchema, { minItems: 1, maxItems: MAX_MEDIA_ACCESS_BATCH }),
}, { additionalProperties: false });
/** Only the items that exist and belong to the caller. A missing target is simply absent, never an error. */
export const MediaAccessListSchema = Type.Object({ items: Type.Array(MediaAccessSchema) }, { additionalProperties: false });
/** One photo or video in the gallery. */
export const MediaItemSchema = Type.Object({
    id: UuidSchema,
    kind: MediaKindSchema,
    mimeType: NonEmptyStringSchema,
    filename: Type.String(),
    /** Of the original, not of the encrypted object in the bucket, which is larger by a tag per chunk. */
    byteSize: Type.Integer({ minimum: 0 }),
    sha256: Sha256Schema,
    width: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    height: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    durationMs: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    /** When the camera says it was taken. Null when the file carried no EXIF, which is common. */
    takenAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    latitude: Type.Union([Type.Number({ minimum: -90, maximum: 90 }), Type.Null()]),
    longitude: Type.Union([Type.Number({ minimum: -180, maximum: 180 }), Type.Null()]),
    createdAt: IsoDateTimeSchema,
    /**
     * When this item was moved to the trash, or null for one in the gallery.
     *
     * On the item rather than on a separate trash shape, because it is a fact
     * about the item and a second shape would be the same fifteen fields with
     * one more. It is what the trash screen counts the window down from - the
     * purge is driven by this timestamp, so anything else shown beside it would
     * be a second opinion about the same date.
     */
    deletedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    /**
     * Which renditions exist for this item, and therefore which variants may be
     * addressed.
     *
     * Sent with the item rather than discovered per tile: a grid that asked
     * would make one request per thumbnail to find out whether it could ask for
     * the thumbnail. Empty on a freshly completed upload, which is what tells
     * the client it still has to produce one - and what stops a tile requesting
     * a `thumb` that does not exist and rendering as a broken image.
     */
    variants: Type.Array(MediaVariantSchema),
}, { additionalProperties: false });
// ---------------------------------------------------------------------------
// Albums
// ---------------------------------------------------------------------------
/**
 * How deep albums may nest.
 *
 * A trip, its days, and the activities in a day is three; eight is room to spare
 * for somebody who thinks in more levels than that. The real ceiling is
 * InnoDB's, which stops a cascading delete at fifteen levels and would leave
 * orphaned rows behind rather than failing - so this sits well below it and the
 * service refuses anything past it at the point the tree would grow.
 */
export const MAX_ALBUM_DEPTH = 8;
/** How many photos one request may put into an album. A selection, not a library. */
export const MAX_ALBUM_ITEMS_PER_REQUEST = 200;
/** One album, as a tree of them is returned. */
export const AlbumSchema = Type.Object({
    id: UuidSchema,
    /** Null for an album at the top level. */
    parentId: Type.Union([UuidSchema, Type.Null()]),
    title: NonEmptyStringSchema,
    /**
     * How far below the top this album sits, counted by the server.
     *
     * Sent rather than derived, because the tree arrives flat and in order: a
     * client rebuilding it from `parentId` alone would have to index the whole
     * list before it could indent the first row.
     */
    depth: Type.Integer({ minimum: 0 }),
    /** How many photos are in this album itself. Deliberately not including its children's. */
    itemCount: Type.Integer({ minimum: 0 }),
    /**
     * The newest photo in the album, for the cover.
     *
     * Only ever an item that **has a thumbnail**, which is what lets a client
     * address `/media/<id>/thumb` without asking first - the same rule a grid
     * tile follows. Null for an empty album, and for one whose photos are all
     * still waiting for a rendition.
     */
    coverMediaItemId: Type.Union([UuidSchema, Type.Null()]),
    /**
     * The group this album is shared with, or null for a private one.
     *
     * Most albums are null and always will be: sharing is something somebody
     * does deliberately to one album, and it does not descend to the albums
     * inside it.
     */
    groupId: Type.Union([UuidSchema, Type.Null()]),
    /**
     * Who shared this album with the reader, or null when it is their own.
     *
     * Present only on an album somebody else owns and has shared into a group
     * the reader is in - which is also the only way another account's album ever
     * appears in this list. An address rather than an id, because the reader is
     * about to be shown that person's photos and "shared by 018f4b3c…" tells
     * them nothing.
     */
    sharedBy: Type.Union([NonEmptyStringSchema, Type.Null()]),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
}, { additionalProperties: false });
/**
 * The whole tree for one account, parents before children.
 *
 * Not paginated, and that is a decision rather than an omission: a tree is only
 * useful whole, the plan's ceiling for the paid tier is two thousand albums, and
 * a page boundary through a subtree would leave a client holding children whose
 * parents it has not seen.
 */
export const AlbumListSchema = Type.Object({ albums: Type.Array(AlbumSchema) }, { additionalProperties: false });
export const CreateAlbumBodySchema = Type.Object({
    title: Type.String({ minLength: 1, maxLength: 200 }),
    /** Omitted or null puts it at the top level. */
    parentId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
}, { additionalProperties: false });
/**
 * A rename, a move, or both.
 *
 * `parentId` is optional so that a rename does not have to restate where the
 * album lives - which matters because `null` is a real destination here, and a
 * client sending it accidentally would move the album to the top level.
 */
export const UpdateAlbumBodySchema = Type.Object({
    title: Type.Optional(Type.String({ minLength: 1, maxLength: 200 })),
    parentId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
}, { additionalProperties: false });
/** Photos to put in an album, or to take out of it. A batch, because a selection is how anybody does this. */
export const AlbumItemsBodySchema = Type.Object({
    mediaItemIds: Type.Array(UuidSchema, { minItems: 1, maxItems: MAX_ALBUM_ITEMS_PER_REQUEST }),
}, { additionalProperties: false });
/**
 * What an add or a remove actually did.
 *
 * `changed` rather than an echo of what was asked for: adding a photo already in
 * the album is a no-op the primary key enforces, and a client that assumed
 * otherwise would tell somebody it filed forty photos when it filed three.
 */
export const AlbumItemsChangedSchema = Type.Object({
    changed: Type.Integer({ minimum: 0 }),
    /** The album's count afterwards, so a list does not need re-fetching to stay honest. */
    itemCount: Type.Integer({ minimum: 0 }),
}, { additionalProperties: false });
// ---------------------------------------------------------------------------
// Groups and sharing
// ---------------------------------------------------------------------------
/**
 * How many invitations one account may send in an hour.
 *
 * The answer to an invitation carries no information at all - the same 202 comes
 * back whether the address belongs to an account here, to nobody, or to somebody
 * who has already declined - so the only thing left to stop bulk probing is how
 * fast the question may be asked. Generous for a person filling a family group,
 * mean for anything working through an address list.
 */
export const MAX_INVITATIONS_PER_HOUR = 20;
/** One sharing group, as a member sees it. */
export const GroupSchema = Type.Object({
    id: UuidSchema,
    name: NonEmptyStringSchema,
    /** Everyone in it, the owner included. Never a count of invitations - see {@link GroupInvitationSchema}. */
    memberCount: Type.Integer({ minimum: 1 }),
    /** Whether the reader created it. The owner may invite, remove members and delete the group; nobody else may. */
    isOwner: Type.Boolean(),
    createdAt: IsoDateTimeSchema,
}, { additionalProperties: false });
export const GroupListSchema = Type.Object({ groups: Type.Array(GroupSchema) }, { additionalProperties: false });
/**
 * One member of a group.
 *
 * The email is here because people in a group together already know who they are
 * sharing with, and a list of opaque ids would be unreadable. It is the **only**
 * place this application discloses an address, and it is disclosed only to
 * people the owner has admitted.
 */
export const GroupMemberSchema = Type.Object({
    userId: UuidSchema,
    email: NonEmptyStringSchema,
    isOwner: Type.Boolean(),
    joinedAt: IsoDateTimeSchema,
}, { additionalProperties: false });
/**
 * A group and who is in it.
 *
 * **There is deliberately no list of pending invitations here.** The sender must
 * not be able to watch one disappear, because that would tell them it was
 * declined - and a decline is the one thing this design promises to keep
 * private. Acceptance is observable because a new member appears; nothing else
 * is.
 */
export const GroupDetailSchema = Type.Object({
    group: GroupSchema,
    members: Type.Array(GroupMemberSchema),
}, { additionalProperties: false });
export const CreateGroupBodySchema = Type.Object({ name: Type.String({ minLength: 1, maxLength: 120 }) }, { additionalProperties: false });
/**
 * Inviting somebody, by their exact address and nothing else.
 *
 * There is no endpoint that searches for a user, and there never will be: the
 * sender has to know the address already. An address belonging to no account
 * here is accepted and does nothing, which is why the response carries no
 * information at all.
 */
export const InviteToGroupBodySchema = Type.Object({ email: EmailSchema }, { additionalProperties: false });
/**
 * An invitation, as the **recipient** sees it. Nobody else ever sees one.
 *
 * It names the group and who sent it, because those are what somebody needs in
 * order to decide - an invitation from an address they do not recognise is one
 * they should be able to decline on sight.
 */
export const GroupInvitationSchema = Type.Object({
    id: UuidSchema,
    groupId: UuidSchema,
    groupName: NonEmptyStringSchema,
    invitedByEmail: NonEmptyStringSchema,
    createdAt: IsoDateTimeSchema,
}, { additionalProperties: false });
export const GroupInvitationListSchema = Type.Object({ invitations: Type.Array(GroupInvitationSchema) }, { additionalProperties: false });
/**
 * Sharing an album with a group, or taking it back.
 *
 * `null` unshares. A `PUT` of the whole relationship rather than two endpoints,
 * because an album is shared with at most one group and "which group" is a
 * single value somebody sets.
 */
export const ShareAlbumBodySchema = Type.Object({ groupId: Type.Union([UuidSchema, Type.Null()]) }, { additionalProperties: false });
// ---------------------------------------------------------------------------
// Album suggestions
// ---------------------------------------------------------------------------
/** Where a suggestion is in its short life. Only `pending` is ever offered; the other two are decided. */
export const ALBUM_SUGGESTION_STATUSES = ['pending', 'accepted', 'dismissed'];
export const AlbumSuggestionStatusSchema = literalUnion(ALBUM_SUGGESTION_STATUSES);
/**
 * An album the worker thinks is in there somewhere.
 *
 * **Facts, never a sentence.** A place, a country, two dates and a count - the
 * client turns those into "38 photos in Annecy, 12-15 August" in its own
 * language, because the worker that found the cluster runs from cron with nobody
 * signed in and no locale to write in. It is also why accepting one sends a
 * title rather than being given one: the title is whatever the person was
 * actually shown, edited or not.
 */
export const AlbumSuggestionSchema = Type.Object({
    id: UuidSchema,
    status: AlbumSuggestionStatusSchema,
    /**
     * The place the cluster is named after, or null.
     *
     * Null in two different situations that are indistinguishable here and
     * deliberately so: no photo in the cluster carried coordinates, or it has a
     * centroid and there is no populated place within the search radius of it.
     * Either way a client has a date range and no place, which is a title it can
     * write.
     */
    placeName: Type.Union([NonEmptyStringSchema, Type.Null()]),
    /** ISO 3166-1 alpha-2. Null exactly when `placeName` is. */
    placeCountry: Type.Union([Type.String({ minLength: 2, maxLength: 2 }), Type.Null()]),
    /** Capture time of the earliest photo in the cluster. */
    startsAt: IsoDateTimeSchema,
    /** Capture time of the latest. Equal to `startsAt` for an outing inside one minute. */
    endsAt: IsoDateTimeSchema,
    /** Photos still in the gallery. A suggestion whose photos were all trashed has none and is not offered. */
    itemCount: Type.Integer({ minimum: 0 }),
    /** The newest photo that has a thumbnail, for the card. Null when none of them has been rendered yet - the album rule exactly. */
    coverMediaItemId: Type.Union([UuidSchema, Type.Null()]),
    createdAt: IsoDateTimeSchema,
}, { additionalProperties: false });
/**
 * Everything currently proposed for one account, most recent outing first.
 *
 * Not paginated, like the album tree and for a plainer reason: this is a short
 * list of things waiting for a yes or a no, and one that ever grew long enough
 * to need a page boundary would be a worker proposing far too much.
 */
export const AlbumSuggestionListSchema = Type.Object({ suggestions: Type.Array(AlbumSuggestionSchema) }, { additionalProperties: false });
/**
 * Turning a suggestion into an album.
 *
 * The title is required and comes from the client, which is the only place a
 * sentence in the right language exists - and it is what was on screen, so
 * somebody who renamed "Annecy" to "Les vacances chez Papi" before pressing the
 * button gets what they typed.
 */
export const AcceptAlbumSuggestionBodySchema = Type.Object({
    title: Type.String({ minLength: 1, maxLength: 200 }),
    /** Omitted or null puts the new album at the top level, as `POST /albums` does. */
    parentId: Type.Optional(Type.Union([UuidSchema, Type.Null()])),
}, { additionalProperties: false });
// ---------------------------------------------------------------------------
// Near-duplicates
// ---------------------------------------------------------------------------
/**
 * A 64-bit perceptual hash, as sixteen lowercase hex characters.
 *
 * **Hex and never a number.** JSON has one numeric type and JavaScript reads it
 * as a double, so any value above `Number.MAX_SAFE_INTEGER` (2^53-1) loses its
 * low bits in transit - and the low bits of a perceptual hash are most of its
 * information. Two different pictures would arrive with the same hash and be
 * reported as duplicates of each other. Lower case is pinned so that the string
 * a client sends and the string a comparison sees cannot differ by case alone.
 */
export const PhashSchema = Type.String({ pattern: '^[0-9a-f]{16}$' });
/**
 * How many of the 64 bits may differ before two pictures are not the same one.
 *
 * Seven, and the number is chosen together with {@link PHASH_BANDS} rather than
 * independently: eight bands of eight bits, and a pair differing in at most
 * seven places can touch at most seven bands, so **at least one band is
 * identical**. Candidate generation by exact band match therefore has no false
 * negatives at all below this threshold - the sweep cannot miss a pair it is
 * meant to find, which is the property worth having, since a missed duplicate is
 * invisible and a spurious one merely costs a glance.
 *
 * Raising it would break that guarantee rather than loosen it: at eight bits a
 * difference the pigeonhole argument no longer holds and recall becomes a matter
 * of luck. Raising both together is possible and costs candidate pairs
 * quadratically, which on this host is the price that actually binds.
 */
export const PHASH_MAX_DISTANCE = 7;
/** Eight bands of eight bits. See {@link PHASH_MAX_DISTANCE} - the two numbers are one decision. */
export const PHASH_BANDS = 8;
/** Where a group is in its short life. Only `open` is ever offered. */
export const DUPLICATE_GROUP_STATUSES = ['open', 'resolved'];
export const DuplicateGroupStatusSchema = literalUnion(DUPLICATE_GROUP_STATUSES);
/**
 * A set of photos the sweep believes are the same picture.
 *
 * **A group rather than a pair**, because near-duplicates chain: three exports of
 * one photograph are three pairs and one thing to decide about. It carries the
 * items themselves rather than their ids, because there is nothing useful to
 * show without them and a second round trip per group would make a screen of ten
 * groups eleven requests.
 */
export const DuplicateGroupSchema = Type.Object({
    id: UuidSchema,
    status: DuplicateGroupStatusSchema,
    /**
     * The photos in it, newest first. Always two or more: a group reduced to one
     * survivor is no longer a duplicate of anything and is not offered.
     */
    items: Type.Array(MediaItemSchema),
    /** When it goes back on the list, or null when it is on it now. */
    remindAfter: Type.Union([IsoDateTimeSchema, Type.Null()]),
    createdAt: IsoDateTimeSchema,
}, { additionalProperties: false });
/**
 * Everything currently proposed for one account.
 *
 * Not paginated, like the album tree and the suggestion list, and for the same
 * plain reason: this is a short list of things waiting for an answer, and one
 * long enough to need a page boundary would be a sweep proposing far too much.
 */
export const DuplicateGroupListSchema = Type.Object({ groups: Type.Array(DuplicateGroupSchema) }, { additionalProperties: false });
/**
 * Which of a group's photos to throw away.
 *
 * They go to the **trash**, not to oblivion - the same thirty-day window every
 * other delete gets, through the same service, so the quota moves exactly once
 * and in the right direction. A duplicate somebody resolves in a hurry is a
 * photo they can still get back.
 *
 * At least one, and the service refuses a request naming every photo in the
 * group: "resolve this by deleting all of them" is not one of the three answers,
 * and a group emptied that way would be indistinguishable from one the sweep
 * should never have proposed.
 */
export const TrashDuplicatesBodySchema = Type.Object({ mediaItemIds: Type.Array(UuidSchema, { minItems: 1, maxItems: 100 }) }, { additionalProperties: false });
/**
 * How long "remind me later" lasts.
 *
 * A fortnight, which is long enough that the question is not the same question
 * again and short enough that "later" still arrives. It is applied on the server
 * rather than sent by the client, because it is a policy rather than a choice -
 * and a client that could name the date could name one in the next century,
 * which is {@link DuplicateGroupSchema}'s "keep both" by another route and
 * without the row that records it.
 */
export const DUPLICATE_REMIND_DAYS = 14;
// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------
/**
 * What a notification can be about.
 *
 * Three kinds, and each earns its place by being something that happens while
 * nobody is looking:
 *
 *  - `group.invitation` - somebody invited you. The one the plan names, and the
 *    only kind that carries actions.
 *  - `group.member_joined` - somebody you invited joined. Told to the group's
 *    owner, and **nothing at all is told when they decline**, which is the
 *    privacy guarantee of the whole group design at its sharpest.
 *  - `album.shared` - a fellow member shared an album into a group you are in.
 *    The only one of the three with no email behind it, so without this you
 *    would simply never learn.
 *
 * Adding a kind is a string here, a payload field if it needs one, and a case in
 * the browser. It is deliberately not an ENUM in the database - see `0028`.
 */
export const NOTIFICATION_KINDS = ['group.invitation', 'group.member_joined', 'album.shared'];
export const NotificationKindSchema = literalUnion(NOTIFICATION_KINDS);
/**
 * How a notification's offer ended.
 *
 * `expired` is the one worth explaining: the group was deleted while an
 * invitation to it was still waiting, so the buttons would have had nothing left
 * to call. Resolving it is what stops the list offering a Join that can only
 * ever answer 404.
 */
export const NOTIFICATION_RESOLUTIONS = ['accepted', 'declined', 'expired'];
export const NotificationResolutionSchema = literalUnion(NOTIFICATION_RESOLUTIONS);
/**
 * One entry in the notification centre.
 *
 * **Facts, never a sentence**, exactly as {@link AlbumSuggestionSchema} is and
 * for the same reason: this row is written by whoever caused the event, in
 * whatever language they happen to use, and read by somebody who may not share
 * it. The browser composes "Claire shared Annecy with Famille" out of the three
 * fields below.
 *
 * `groupName` and `albumTitle` are copies rather than joins, and the copy is the
 * point: the group or album may have been deleted since, and an entry that
 * renders as a blank space is worse than one that says what it said at the time.
 *
 * `actorEmail` discloses an address, and in all three kinds it is an address
 * already disclosed by an existing rule - the sender of an invitation is shown
 * on {@link GroupInvitationSchema}, and the other two actors are fellow members,
 * whom {@link GroupMemberSchema} already names.
 */
export const NotificationSchema = Type.Object({
    id: UuidSchema,
    kind: NotificationKindSchema,
    /** The invitation or album it is about. Null only for a kind that is about nothing in particular. */
    subjectId: Type.Union([UuidSchema, Type.Null()]),
    /** Whoever caused it, or null if that account has since been deleted. */
    actorEmail: Type.Union([NonEmptyStringSchema, Type.Null()]),
    /** The group as it was named at the time. Null for a kind that is not about a group. */
    groupName: Type.Union([NonEmptyStringSchema, Type.Null()]),
    /** The album as it was titled at the time. Null for a kind that is not about an album. */
    albumTitle: Type.Union([NonEmptyStringSchema, Type.Null()]),
    /** When it was seen. Null means it is counted on the bell. */
    readAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    /** When its offer was taken up or withdrawn. Null means the actions, if it has any, are still live. */
    resolvedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    resolution: Type.Union([NotificationResolutionSchema, Type.Null()]),
    createdAt: IsoDateTimeSchema,
}, { additionalProperties: false });
/**
 * A page of the notification centre, newest first.
 *
 * Cursor-paginated on `(created_at, id)` for the reason the gallery is: entries
 * arrive at the top while somebody is reading, and an offset boundary under a
 * growing list shows them the same card twice.
 */
export const NotificationListSchema = paginatedSchema(NotificationSchema);
/**
 * What the bell shows.
 *
 * Its own endpoint rather than a field on the list, because this is the one the
 * SPA asks on every focus and it must not drag a page of rows behind it. An
 * object rather than a bare number so it can grow a field without a new route.
 */
export const NotificationSummarySchema = Type.Object({ unread: Type.Integer({ minimum: 0 }) }, { additionalProperties: false });
/**
 * Marking notifications seen.
 *
 * Omitting `ids` marks everything unread as read, which is what opening the
 * panel does - naming every id on screen instead would race the entries
 * arriving behind it and leave a bell that never quite reaches zero.
 */
export const MarkNotificationsReadBodySchema = Type.Object({ ids: Type.Optional(Type.Array(UuidSchema, { maxItems: 200 })) }, { additionalProperties: false });
/** How many were still unread when the request arrived. Zero is the ordinary answer to a second press. */
export const NotificationsReadSchema = Type.Object({ read: Type.Integer({ minimum: 0 }) }, { additionalProperties: false });
/**
 * A page of the library, newest first.
 *
 * Cursor-paginated rather than offset, which for this list is a correctness
 * requirement and not only a performance one: the gallery grows at the top as
 * uploads arrive, so an offset page boundary shifts under a reader who is
 * scrolling and shows them duplicates. The cursor is the `(sort_at, id)` pair
 * `idx_media_items_owner_sort` is built on.
 */
export const MediaListResponseSchema = paginatedSchema(MediaItemSchema);
// ---------------------------------------------------------------------------
// Devices, and the folders they back up
// ---------------------------------------------------------------------------
/** What a device runs. Android is built; iOS is planned, and costs nothing to name. */
export const DEVICE_PLATFORMS = ['android', 'ios'];
export const DevicePlatformSchema = literalUnion(DEVICE_PLATFORMS);
/**
 * Which connections a folder may upload over.
 *
 * `unmetered` rather than "Wi-Fi", because that is the question the phone can
 * actually answer and the one that matters: a Wi-Fi hotspot on somebody else's
 * phone is metered, and an Ethernet dongle is not.
 */
export const SYNC_NETWORKS = ['unmetered', 'any'];
export const SyncNetworkSchema = literalUnion(SYNC_NETWORKS);
/** More folders than anyone backs up, and few enough that a replaced set is one small write. */
export const MAX_SYNC_FOLDERS = 64;
/**
 * One folder a device backs up.
 *
 * `key` is MediaStore's `BUCKET_ID`, sent as a string: the phone's name for the
 * folder, stable while the folder exists and different for two folders of the
 * same name on two volumes. The server never interprets it.
 */
export const SyncFolderSchema = Type.Object({
    key: Type.String({ minLength: 1, maxLength: 64 }),
    label: Type.String({ minLength: 1, maxLength: 255 }),
    network: SyncNetworkSchema,
}, { additionalProperties: false });
/**
 * Body of `PUT /photos/devices/:id` - a device describing itself and its whole
 * folder set, which replaces whatever was there.
 *
 * The whole set rather than a change to it, because what the phone knows is its
 * settings screen, not a diff: sending what it has means a request repeated by a
 * flaky network, or one whose answer was lost, cannot leave the two disagreeing.
 * An empty list is a device with auto-upload switched off.
 */
export const PutDeviceBodySchema = Type.Object({
    platform: DevicePlatformSchema,
    label: Type.String({ minLength: 1, maxLength: 120 }),
    appVersion: Type.String({ minLength: 1, maxLength: 32 }),
    folders: Type.Array(SyncFolderSchema, { maxItems: MAX_SYNC_FOLDERS }),
}, { additionalProperties: false });
/** A device as its owner sees it, and nobody else ever does. */
export const DeviceSchema = Type.Object({
    /** Chosen by the app when it was installed. See `0030_photos_devices.sql` for why. */
    id: UuidSchema,
    platform: DevicePlatformSchema,
    label: Type.String(),
    appVersion: Type.String(),
    folders: Type.Array(SyncFolderSchema),
    /** When a background run last reported in. Null for a device that has not run yet. */
    lastRunAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    /** What that run sent. */
    lastRunUploaded: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    /** What it left for the next one - a large number on a first backup, zero once caught up. */
    lastRunPending: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    createdAt: IsoDateTimeSchema,
}, { additionalProperties: false });
export const DeviceListSchema = Type.Object({ devices: Type.Array(DeviceSchema) }, { additionalProperties: false });
/**
 * Body of `POST /photos/devices/:id/runs` - one background run, reported at its
 * end.
 *
 * Answered 404 when the device has been removed, and that answer is the whole
 * of how removing a phone from the account stops it: the phone reads it as
 * "switch auto-upload off", and does.
 */
export const DeviceRunBodySchema = Type.Object({
    uploaded: Type.Integer({ minimum: 0, maximum: 1_000_000 }),
    pending: Type.Integer({ minimum: 0, maximum: 100_000_000 }),
}, { additionalProperties: false });
//# sourceMappingURL=photos.js.map